import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { NotePencil, ListChecks, Plus, Folder, FolderPlus, PencilSimple, Trash, X, Image, UserCircle, Flag, CheckCircle, Circle, Camera, ArrowCounterClockwise, CheckSquare, Funnel } from '@phosphor-icons/react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITY_CONFIG = {
  basse: { label: 'Basse', color: '#5E5F66', bg: '#F4F5F7' },
  normal: { label: 'Normale', color: '#1565C0', bg: '#E3F2FD' },
  haute: { label: 'Haute', color: '#EF6C00', bg: '#FFF3E0' },
  urgente: { label: 'Urgente', color: '#C62828', bg: '#FFEBEE' },
};
const COLOR_PRESETS = ['#1565C0', '#2E7D32', '#EF6C00', '#C62828', '#7B1FA2', '#00796B', '#5D4037'];
const firstSentence = (txt = '') => {
  // Strip HTML tags before taking the first sentence (content may now be rich HTML)
  const plain = (txt || '').replace(/<[^>]+>/g, ' ').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
  if (!plain) return '';
  const m = plain.match(/^[^.!?\n]+[.!?]?/);
  return (m ? m[0] : plain).slice(0, 140);
};

// ---- Bulk action small modal (used for move + assign) ----
const BulkActionDialog = ({ title, onClose, children }) => (
  <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100 }} onClick={onClose}>
    <div className="card" style={{ width: '100%', maxWidth: '460px', maxHeight: '85vh', overflow: 'auto' }} onClick={(e) => e.stopPropagation()}>
      <div style={{ padding: '14px 18px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h3 style={{ margin: 0, fontSize: '1rem' }}>{title}</h3>
        <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }} data-testid="bulk-dialog-close"><X size={20} /></button>
      </div>
      <div style={{ padding: '16px 18px' }}>{children}</div>
    </div>
  </div>
);

// ---- Folder Sidebar ----
const FolderSidebar = ({ folders, currentFolder, onSelect, onCreate, onDelete, onEdit, onDropToFolder, type }) => {
  const [showAdd, setShowAdd] = useState(false);
  const [name, setName] = useState('');
  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return;
    await onCreate(name.trim());
    setName('');
    setShowAdd(false);
  };
  return (
    <div data-testid={`folders-sidebar-${type}`} className="card" style={{ padding: '14px', minWidth: '220px', maxWidth: '260px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <div style={{ fontWeight: 700, fontSize: '0.9rem' }}>Dossiers</div>
        <button className="btn btn-secondary btn-sm" onClick={() => setShowAdd(s => !s)} title="Nouveau dossier" data-testid={`new-folder-btn-${type}`}><FolderPlus size={14} /></button>
      </div>
      {showAdd && (
        <form onSubmit={submit} style={{ display: 'flex', gap: '4px', marginBottom: '10px' }}>
          <input type="text" className="form-input" placeholder="Nom" value={name} onChange={(e) => setName(e.target.value)} autoFocus style={{ flex: 1, fontSize: '0.82rem', padding: '6px 8px' }} data-testid={`folder-name-input-${type}`} />
          <button type="submit" className="btn btn-primary btn-sm">OK</button>
        </form>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
        <FolderItem active={currentFolder === 'all'} onClick={() => onSelect('all')} icon="all" label="Tous" testId={`folder-all-${type}`} />
        <FolderItem active={currentFolder === 'none'} onClick={() => onSelect('none')} icon="none" label="Sans dossier" testId={`folder-none-${type}`} onDrop={() => onDropToFolder && onDropToFolder('')} />
        {folders.filter(f => !f.is_trash && f.id !== 'trash').map(f => (
          <FolderItem
            key={f.id}
            active={currentFolder === f.id}
            onClick={() => onSelect(f.id)}
            label={f.name}
            count={f.count}
            color={f.color}
            onDelete={() => onDelete(f.id)}
            onEdit={() => onEdit(f)}
            onDrop={() => onDropToFolder && onDropToFolder(f.id)}
            testId={`folder-${f.id}`}
          />
        ))}
        {/* Trash folder always rendered last */}
        {folders.find(f => f.is_trash || f.id === 'trash') && (
          <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid #E5E7EB' }}>
            <FolderItem
              active={currentFolder === 'trash'}
              onClick={() => onSelect('trash')}
              label="Corbeille"
              count={folders.find(f => f.is_trash || f.id === 'trash')?.count ?? 0}
              color="#9E9EA7"
              isTrash
              onDrop={() => onDropToFolder && onDropToFolder('__trash__')}
              testId={`folder-trash-${type}`}
            />
          </div>
        )}
      </div>
      <div style={{ marginTop: 10, fontSize: '0.7rem', color: '#9CA3AF', fontStyle: 'italic' }}>
        Astuce : glissez une note/tache sur un dossier pour la deplacer.
      </div>
    </div>
  );
};
const FolderItem = ({ active, onClick, label, count, color = '#1565C0', icon, onDelete, onEdit, onDrop, testId, isTrash }) => {
  const [over, setOver] = useState(false);
  return (
    <div
      onClick={onClick}
      data-testid={testId}
      onDragOver={onDrop ? (e) => { e.preventDefault(); setOver(true); } : undefined}
      onDragLeave={onDrop ? () => setOver(false) : undefined}
      onDrop={onDrop ? (e) => {
        e.preventDefault();
        setOver(false);
        onDrop(e);
      } : undefined}
      style={{
        display: 'flex', alignItems: 'center', gap: '8px',
        padding: '8px 10px', borderRadius: '6px', cursor: 'pointer',
        backgroundColor: over ? `${color}30` : (active ? `${color}20` : 'transparent'),
        border: over ? `2px dashed ${color}` : (active ? `1px solid ${color}` : '1px solid transparent'),
        fontSize: '0.85rem',
        transition: 'background-color 120ms, border-color 120ms',
      }}
      onMouseEnter={(e) => { if (!active && !over) e.currentTarget.style.backgroundColor = '#F4F5F7'; }}
      onMouseLeave={(e) => { if (!active && !over) e.currentTarget.style.backgroundColor = 'transparent'; }}
    >
      {isTrash
        ? <Trash size={16} weight={active ? 'fill' : 'regular'} color={color} />
        : <Folder size={16} weight={active ? 'fill' : 'regular'} color={color} />}
      <span style={{ flex: 1, fontWeight: active ? 700 : 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
      {count !== undefined && <span style={{ fontSize: '0.72rem', color: '#5E5F66' }}>{count}</span>}
      {onEdit && <PencilSimple size={12} onClick={(e) => { e.stopPropagation(); onEdit(); }} style={{ cursor: 'pointer' }} />}
      {onDelete && <Trash size={12} onClick={(e) => { e.stopPropagation(); if (window.confirm('Supprimer ce dossier ? Les elements seront deplaces hors dossier.')) onDelete(); }} style={{ cursor: 'pointer', color: '#C62828' }} />}
    </div>
  );
};

// ---- Note Modal ----
const NoteModal = ({ note, folders, employees, onClose, onSubmit, onDelete }) => {
  const [form, setForm] = useState(() => note ? { ...note } : { title: '', date: new Date().toISOString().slice(0, 10), content: '', folder_id: '', assigned_user_id: '' });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(note?.photo_url ? `${API_URL}${note.photo_url}?v=${note.id}` : '');
  const [submitting, setSubmitting] = useState(false);
  const handleFile = (e) => {
    const f = e.target.files?.[0];
    if (f) { setPhoto(f); setPhotoPreview(URL.createObjectURL(f)); }
  };
  const removePhoto = async () => {
    if (note?.id && note?.photo_url) {
      await axios.delete(`${API_URL}/api/planning/notes/${note.id}/photo`, { withCredentials: true });
    }
    setPhoto(null); setPhotoPreview('');
  };
  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const id = await onSubmit(form);
      const noteId = id || note?.id;
      if (photo && noteId) {
        const fd = new FormData();
        fd.append('photo', photo);
        await axios.post(`${API_URL}/api/planning/notes/${noteId}/photo`, fd, { withCredentials: true, headers: { 'Content-Type': 'multipart/form-data' } });
      }
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>{note?.id ? 'Modifier la note' : 'Nouvelle note'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
        </div>
        <form onSubmit={submit} style={{ padding: '20px' }}>
          <div className="form-group">
            <label className="form-label">Titre</label>
            <input type="text" className="form-input" value={form.title || ''} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Titre de la note" data-testid="note-title-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Contenu</label>
            <textarea className="form-input" value={form.content || ''} onChange={(e) => setForm({ ...form, content: e.target.value })} rows={6} required data-testid="note-content-input" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="form-input" value={form.date || ''} onChange={(e) => setForm({ ...form, date: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Dossier</label>
              <Select value={form.folder_id || 'none'} onValueChange={(v) => setForm({ ...form, folder_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Aucun dossier</SelectItem>
                  {folders.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Affecter a un salarie</label>
            <Select value={form.assigned_user_id || 'none'} onValueChange={(v) => setForm({ ...form, assigned_user_id: v === 'none' ? '' : v })}>
              <SelectTrigger data-testid="note-assign-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {employees.map(e => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="form-group">
            <label className="form-label">Photo</label>
            {photoPreview ? (
              <div style={{ position: 'relative', width: '160px', borderRadius: '8px', overflow: 'hidden', border: '1px solid #E2E2E6' }}>
                <img src={photoPreview} alt="" style={{ width: '100%', display: 'block' }} />
                <button type="button" onClick={removePhoto} style={{ position: 'absolute', top: '4px', right: '4px', background: 'rgba(0,0,0,0.6)', color: 'white', border: 'none', borderRadius: '50%', width: '24px', height: '24px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 0 }}><X size={14} /></button>
              </div>
            ) : (
              <input type="file" accept="image/*" onChange={handleFile} data-testid="note-photo-input" />
            )}
          </div>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
            <div>
              {note?.id && onDelete && <button type="button" className="btn btn-danger" onClick={() => { if (window.confirm('Supprimer cette note ?')) onDelete(note.id); }}><Trash size={16} /></button>}
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn btn-primary" disabled={submitting} data-testid="note-submit-btn">{submitting ? '...' : (note?.id ? 'Modifier' : 'Creer')}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Task Modal ----
const TaskModal = ({ task, folders, employees, onClose, onSubmit, onDelete }) => {
  const [form, setForm] = useState(() => task ? { ...task, assigned_user_ids: task.assigned_user_ids || [] } : {
    title: '', description: '', date: new Date().toISOString().slice(0, 10), time: '09:00',
    reminder: true, color: '#1565C0', priority: 'normal', folder_id: '', assigned_user_ids: []
  });
  const toggleUser = (uid) => {
    const list = form.assigned_user_ids || [];
    setForm({ ...form, assigned_user_ids: list.includes(uid) ? list.filter(x => x !== uid) : [...list, uid] });
  };
  return (
    <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
      <div className="card" style={{ width: '100%', maxWidth: '620px', maxHeight: '90vh', overflow: 'auto' }}>
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #E2E2E6', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <h3 style={{ margin: 0 }}>{task?.id ? 'Modifier la tache' : 'Nouvelle tache'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer' }}><X size={22} /></button>
        </div>
        <form onSubmit={(e) => { e.preventDefault(); onSubmit(form); }} style={{ padding: '20px' }}>
          <div className="form-group">
            <label className="form-label">Titre *</label>
            <input type="text" className="form-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="task-title-input" />
          </div>
          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea className="form-input" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} data-testid="task-desc-input" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px' }}>
            <div className="form-group">
              <label className="form-label">Date</label>
              <input type="date" className="form-input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
            </div>
            <div className="form-group">
              <label className="form-label">Heure</label>
              <input type="time" className="form-input" value={form.time} onChange={(e) => setForm({ ...form, time: e.target.value })} />
            </div>
            <div className="form-group">
              <label className="form-label">Priorite</label>
              <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
                <SelectTrigger data-testid="task-priority-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(PRIORITY_CONFIG).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Couleur</label>
            <div style={{ display: 'flex', gap: '8px' }}>
              {COLOR_PRESETS.map(c => (
                <div key={c} onClick={() => setForm({ ...form, color: c })} data-testid={`color-${c}`} style={{ width: '28px', height: '28px', borderRadius: '50%', backgroundColor: c, cursor: 'pointer', border: form.color === c ? '3px solid #1E2030' : '2px solid #E2E2E6' }} />
              ))}
            </div>
          </div>
          <div className="form-group">
            <label className="form-label">Dossier</label>
            <Select value={form.folder_id || 'none'} onValueChange={(v) => setForm({ ...form, folder_id: v === 'none' ? '' : v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun dossier</SelectItem>
                {folders.map(f => <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="form-group">
            <label className="form-label">Assignes ({(form.assigned_user_ids || []).length})</label>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '120px', overflowY: 'auto', padding: '6px', border: '1px solid #E2E2E6', borderRadius: '6px' }}>
              {employees.map(emp => {
                const sel = (form.assigned_user_ids || []).includes(emp.id);
                return (
                  <span key={emp.id} onClick={() => toggleUser(emp.id)} data-testid={`assign-${emp.id}`} style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '0.78rem', cursor: 'pointer', backgroundColor: sel ? '#002FA7' : '#F4F5F7', color: sel ? 'white' : '#1E2030', fontWeight: sel ? 600 : 400 }}>
                    {emp.name}
                  </span>
                );
              })}
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginTop: '8px', cursor: 'pointer' }}>
            <input type="checkbox" checked={!!form.reminder} onChange={(e) => setForm({ ...form, reminder: e.target.checked })} />
            Notifier les assignes
          </label>
          <div style={{ display: 'flex', gap: '10px', justifyContent: 'space-between', marginTop: '20px' }}>
            <div>{task?.id && onDelete && <button type="button" className="btn btn-danger" onClick={() => { if (window.confirm('Supprimer ?')) onDelete(task.id); }}><Trash size={16} /></button>}</div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button type="button" className="btn btn-secondary" onClick={onClose}>Annuler</button>
              <button type="submit" className="btn btn-primary" data-testid="task-submit-btn">{task?.id ? 'Modifier' : 'Creer'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

// ---- Main page ----
const NotesTasksPage = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  const [activeTab, setActiveTab] = useState('notes');
  const [employees, setEmployees] = useState([]);
  // Filter "by assigned person" (admin only)
  const [filterUserId, setFilterUserId] = useState('all');
  // Notes
  const [notes, setNotes] = useState([]);
  const [noteFolders, setNoteFolders] = useState([]);
  const [currentNoteFolder, setCurrentNoteFolder] = useState('all');
  const [editingNote, setEditingNote] = useState(null);
  const [showNoteModal, setShowNoteModal] = useState(false);
  // Tasks
  const [tasks, setTasks] = useState([]);
  const [taskFolders, setTaskFolders] = useState([]);
  const [currentTaskFolder, setCurrentTaskFolder] = useState('all');
  const [editingTask, setEditingTask] = useState(null);
  const [showTaskModal, setShowTaskModal] = useState(false);
  const [expandedTaskId, setExpandedTaskId] = useState(null);
  // Multi-select state (separate per tab)
  const [selectMode, setSelectMode] = useState(false);
  const [selectedNoteIds, setSelectedNoteIds] = useState(new Set());
  const [selectedTaskIds, setSelectedTaskIds] = useState(new Set());

  const toggleNoteSelected = (id) => {
    setSelectedNoteIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const toggleTaskSelected = (id) => {
    setSelectedTaskIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const exitSelectMode = () => {
    setSelectMode(false);
    setSelectedNoteIds(new Set());
    setSelectedTaskIds(new Set());
  };
  // Reset selection on tab change
  useEffect(() => { exitSelectMode(); }, [activeTab]);

  const fetchNotes = useCallback(async () => {
    try {
      const folderParam = currentNoteFolder === 'all' ? '' : currentNoteFolder;
      const params = {};
      if (folderParam) params.folder_id = folderParam;
      if (isAdmin && filterUserId && filterUserId !== 'all') params.assigned_user_id = filterUserId;
      const res = await axios.get(`${API_URL}/api/planning/notes`, { params, withCredentials: true });
      setNotes(res.data || []);
    } catch { toast.error('Erreur chargement notes'); }
  }, [currentNoteFolder, filterUserId, isAdmin]);
  const fetchTasks = useCallback(async () => {
    try {
      const folderParam = currentTaskFolder === 'all' ? '' : currentTaskFolder;
      const params = {};
      if (folderParam) params.folder_id = folderParam;
      if (isAdmin && filterUserId && filterUserId !== 'all') params.assigned_user_id = filterUserId;
      const res = await axios.get(`${API_URL}/api/planning/tasks-reminders`, { params, withCredentials: true });
      setTasks(res.data || []);
    } catch { toast.error('Erreur chargement taches'); }
  }, [currentTaskFolder, filterUserId, isAdmin]);
  const fetchFolders = useCallback(async () => {
    try {
      const [n, t] = await Promise.all([
        axios.get(`${API_URL}/api/planning/folders`, { params: { folder_type: 'note' }, withCredentials: true }),
        axios.get(`${API_URL}/api/planning/folders`, { params: { folder_type: 'task' }, withCredentials: true }),
      ]);
      setNoteFolders(n.data || []);
      setTaskFolders(t.data || []);
    } catch { /* noop */ }
  }, []);
  const fetchEmployees = useCallback(async () => {
    try {
      // Include admins in the list so the "filter by person" dropdown and the
      // assign select can show every user of the company.
      const res = await axios.get(`${API_URL}/api/users/employees`, { params: { include_admins: true }, withCredentials: true });
      setEmployees(res.data || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => { fetchEmployees(); fetchFolders(); }, [fetchEmployees, fetchFolders]);
  useEffect(() => { fetchNotes(); }, [fetchNotes]);
  useEffect(() => { fetchTasks(); }, [fetchTasks]);

  // ---- Folder actions ----
  const createFolder = async (type, name) => {
    try {
      await axios.post(`${API_URL}/api/planning/folders`, { name, type }, { withCredentials: true });
      toast.success('Dossier cree');
      fetchFolders();
    } catch { toast.error('Erreur'); }
  };
  const deleteFolder = async (type, id) => {
    try {
      await axios.delete(`${API_URL}/api/planning/folders/${id}`, { withCredentials: true });
      toast.success('Dossier supprime');
      if (type === 'note' && currentNoteFolder === id) setCurrentNoteFolder('all');
      if (type === 'task' && currentTaskFolder === id) setCurrentTaskFolder('all');
      fetchFolders(); fetchNotes(); fetchTasks();
    } catch { toast.error('Erreur'); }
  };
  const editFolder = async (folder) => {
    const newName = window.prompt('Nouveau nom du dossier :', folder.name);
    if (!newName || newName === folder.name) return;
    try {
      await axios.put(`${API_URL}/api/planning/folders/${folder.id}`, { name: newName }, { withCredentials: true });
      toast.success('Dossier mis a jour');
      fetchFolders();
    } catch { toast.error('Erreur'); }
  };

  // ---- Drag & Drop into folders ----
  const [dragged, setDragged] = useState(null); // { type: 'note'|'task', id, current_folder_id }
  const moveNoteToFolder = async (folderId) => {
    if (!dragged || dragged.type !== 'note') return;
    if (folderId === '__trash__') {
      // Drag-to-trash: soft-delete
      try {
        await axios.delete(`${API_URL}/api/planning/notes/${dragged.id}`, { withCredentials: true });
        toast.success('Note deplacee dans la corbeille');
        fetchNotes(); fetchFolders();
      } catch { toast.error('Erreur deplacement'); }
      finally { setDragged(null); }
      return;
    }
    if ((dragged.current_folder_id || '') === (folderId || '')) { setDragged(null); return; }
    try {
      await axios.put(`${API_URL}/api/planning/notes/${dragged.id}`, { folder_id: folderId || null }, { withCredentials: true });
      toast.success(folderId ? 'Note deplacee' : 'Note retiree du dossier');
      fetchNotes(); fetchFolders();
    } catch { toast.error('Erreur deplacement'); }
    finally { setDragged(null); }
  };
  const moveTaskToFolder = async (folderId) => {
    if (!dragged || dragged.type !== 'task') return;
    if (folderId === '__trash__') {
      try {
        await axios.delete(`${API_URL}/api/planning/tasks-reminders/${dragged.id}`, { withCredentials: true });
        toast.success('Tache deplacee dans la corbeille');
        fetchTasks(); fetchFolders();
      } catch { toast.error('Erreur deplacement'); }
      finally { setDragged(null); }
      return;
    }
    if ((dragged.current_folder_id || '') === (folderId || '')) { setDragged(null); return; }
    try {
      await axios.put(`${API_URL}/api/planning/tasks-reminders/${dragged.id}`, { folder_id: folderId || null }, { withCredentials: true });
      toast.success(folderId ? 'Tache deplacee' : 'Tache retiree du dossier');
      fetchTasks(); fetchFolders();
    } catch { toast.error('Erreur deplacement'); }
    finally { setDragged(null); }
  };

  // ---- Note actions ----
  const submitNote = async (form) => {
    try {
      if (editingNote?.id) {
        await axios.put(`${API_URL}/api/planning/notes/${editingNote.id}`, form, { withCredentials: true });
        toast.success('Note mise a jour');
        fetchNotes();
        return editingNote.id;
      } else {
        const res = await axios.post(`${API_URL}/api/planning/notes`, form, { withCredentials: true });
        toast.success('Note creee');
        fetchNotes();
        return res.data.id;
      }
    } catch { toast.error('Erreur'); }
  };

  // Create a blank note then navigate straight to the full-page editor
  const createAndOpenNote = async () => {
    try {
      const today = new Date().toISOString().slice(0, 10);
      const res = await axios.post(
        `${API_URL}/api/planning/notes`,
        { title: '', content: '', date: today, folder_id: currentNoteFolder !== 'all' && currentNoteFolder !== 'trash' && currentNoteFolder !== 'none' ? currentNoteFolder : '' },
        { withCredentials: true },
      );
      navigate(`/notes-taches/${res.data.id}`);
    } catch { toast.error('Erreur création note'); }
  };

  const openNoteDetail = (id) => navigate(`/notes-taches/${id}`);
  const deleteNote = async (id) => {
    try {
      const res = await axios.delete(`${API_URL}/api/planning/notes/${id}`, { withCredentials: true });
      toast.success(res.data?.message || 'Note deplacee dans la corbeille');
      setShowNoteModal(false);
      fetchNotes(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };
  const restoreNote = async (id) => {
    try {
      await axios.post(`${API_URL}/api/planning/notes/${id}/restore`, {}, { withCredentials: true });
      toast.success('Note restauree');
      fetchNotes(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };
  const purgeNote = async (id) => {
    if (!window.confirm('Supprimer DEFINITIVEMENT cette note ?')) return;
    try {
      await axios.delete(`${API_URL}/api/planning/notes/${id}?hard=true`, { withCredentials: true });
      toast.success('Note supprimee definitivement');
      fetchNotes(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };
  const emptyNotesTrash = async () => {
    if (!window.confirm('Vider definitivement la corbeille des notes ?')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/planning/notes/trash/empty`, { withCredentials: true });
      toast.success(`${res.data?.deleted || 0} note(s) supprimee(s)`);
      fetchNotes(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };

  // ---- Task actions ----
  const submitTask = async (form) => {
    try {
      if (editingTask?.id) {
        await axios.put(`${API_URL}/api/planning/tasks-reminders/${editingTask.id}`, form, { withCredentials: true });
        toast.success('Tache mise a jour');
      } else {
        await axios.post(`${API_URL}/api/planning/tasks-reminders`, form, { withCredentials: true });
        toast.success('Tache creee');
      }
      setShowTaskModal(false);
      fetchTasks();
    } catch { toast.error('Erreur'); }
  };
  const toggleTaskComplete = async (task) => {
    try {
      await axios.put(`${API_URL}/api/planning/tasks-reminders/${task.id}`, { completed: !task.completed }, { withCredentials: true });
      fetchTasks();
    } catch { toast.error('Erreur'); }
  };
  const deleteTask = async (id) => {
    try {
      const res = await axios.delete(`${API_URL}/api/planning/tasks-reminders/${id}`, { withCredentials: true });
      toast.success(res.data?.message || 'Tache deplacee dans la corbeille');
      setShowTaskModal(false);
      fetchTasks(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };
  const restoreTask = async (id) => {
    try {
      await axios.post(`${API_URL}/api/planning/tasks-reminders/${id}/restore`, {}, { withCredentials: true });
      toast.success('Tache restauree');
      fetchTasks(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };
  const purgeTask = async (id) => {
    if (!window.confirm('Supprimer DEFINITIVEMENT cette tache ?')) return;
    try {
      await axios.delete(`${API_URL}/api/planning/tasks-reminders/${id}?hard=true`, { withCredentials: true });
      toast.success('Tache supprimee definitivement');
      fetchTasks(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };
  const emptyTasksTrash = async () => {
    if (!window.confirm('Vider definitivement la corbeille des taches ?')) return;
    try {
      const res = await axios.delete(`${API_URL}/api/planning/tasks-reminders/trash/empty`, { withCredentials: true });
      toast.success(`${res.data?.deleted || 0} tache(s) supprimee(s)`);
      fetchTasks(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };

  // ---- Bulk delete ----
  const bulkDeleteNotes = async () => {
    const ids = Array.from(selectedNoteIds);
    if (ids.length === 0) { toast.info('Aucune note selectionnee'); return; }
    const inTrash = currentNoteFolder === 'trash';
    const msg = inTrash
      ? `Supprimer DEFINITIVEMENT ${ids.length} note(s) ?`
      : `Deplacer ${ids.length} note(s) dans la corbeille ?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.post(`${API_URL}/api/planning/notes/bulk-delete`, { ids, hard: inTrash }, { withCredentials: true });
      toast.success(`${res.data?.deleted || 0} note(s) ${inTrash ? 'supprimee(s)' : 'deplacee(s)'}`);
      exitSelectMode();
      fetchNotes(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };
  const bulkDeleteTasks = async () => {
    const ids = Array.from(selectedTaskIds);
    if (ids.length === 0) { toast.info('Aucune tache selectionnee'); return; }
    const inTrash = currentTaskFolder === 'trash';
    const msg = inTrash
      ? `Supprimer DEFINITIVEMENT ${ids.length} tache(s) ?`
      : `Deplacer ${ids.length} tache(s) dans la corbeille ?`;
    if (!window.confirm(msg)) return;
    try {
      const res = await axios.post(`${API_URL}/api/planning/tasks-reminders/bulk-delete`, { ids, hard: inTrash }, { withCredentials: true });
      toast.success(`${res.data?.deleted || 0} tache(s) ${inTrash ? 'supprimee(s)' : 'deplacee(s)'}`);
      exitSelectMode();
      fetchTasks(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };
  const selectAllVisibleNotes = () => {
    setSelectedNoteIds(new Set(notes.map(n => n.id)));
  };
  const selectAllVisibleTasks = () => {
    setSelectedTaskIds(new Set(tasks.map(t => t.id)));
  };

  // ---- Bulk move / Bulk assign ----
  const [bulkAction, setBulkAction] = useState(null); // 'move' | 'assign' | null

  const bulkMoveNotes = async (folderId) => {
    const ids = Array.from(selectedNoteIds);
    if (ids.length === 0) return;
    try {
      const res = await axios.post(`${API_URL}/api/planning/notes/bulk-move`, { ids, folder_id: folderId || null }, { withCredentials: true });
      toast.success(`${res.data?.moved || 0} note(s) deplacee(s)`);
      setBulkAction(null); exitSelectMode();
      fetchNotes(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };
  const bulkMoveTasks = async (folderId) => {
    const ids = Array.from(selectedTaskIds);
    if (ids.length === 0) return;
    try {
      const res = await axios.post(`${API_URL}/api/planning/tasks-reminders/bulk-move`, { ids, folder_id: folderId || null }, { withCredentials: true });
      toast.success(`${res.data?.moved || 0} tache(s) deplacee(s)`);
      setBulkAction(null); exitSelectMode();
      fetchTasks(); fetchFolders();
    } catch { toast.error('Erreur'); }
  };
  const bulkAssignNotes = async (userId) => {
    const ids = Array.from(selectedNoteIds);
    if (ids.length === 0) return;
    try {
      const res = await axios.post(`${API_URL}/api/planning/notes/bulk-assign`, { ids, assigned_user_id: userId || null }, { withCredentials: true });
      toast.success(`${res.data?.updated || 0} note(s) affectee(s)`);
      setBulkAction(null); exitSelectMode();
      fetchNotes();
    } catch { toast.error('Erreur'); }
  };
  const bulkAssignTasks = async (userIds) => {
    const ids = Array.from(selectedTaskIds);
    if (ids.length === 0) return;
    try {
      const res = await axios.post(`${API_URL}/api/planning/tasks-reminders/bulk-assign`, { ids, assigned_user_ids: userIds }, { withCredentials: true });
      toast.success(`${res.data?.updated || 0} tache(s) affectee(s)`);
      setBulkAction(null); exitSelectMode();
      fetchTasks();
    } catch { toast.error('Erreur'); }
  };
  const [bulkAssignTaskUserIds, setBulkAssignTaskUserIds] = useState([]);
  const toggleBulkAssignTaskUser = (uid) => {
    setBulkAssignTaskUserIds(prev => prev.includes(uid) ? prev.filter(x => x !== uid) : [...prev, uid]);
  };

  return (
    <div data-testid="notes-taches-page">
      <div className="page-header">
        <h1 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          {activeTab === 'notes' ? <NotePencil size={28} weight="duotone" /> : <ListChecks size={28} weight="duotone" />}
          Notes & Taches
        </h1>
        <p className="page-subtitle">Centralisez vos notes et vos taches dans des dossiers separes.</p>
      </div>

      <div style={{ display: 'flex', gap: '0', borderBottom: '2px solid #E2E2E6', marginBottom: '20px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => setActiveTab('notes')} data-testid="tab-notes" style={{ padding: '12px 24px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontWeight: activeTab === 'notes' ? 700 : 400, borderBottom: activeTab === 'notes' ? '2px solid #002FA7' : '2px solid transparent', marginBottom: '-2px', color: activeTab === 'notes' ? '#002FA7' : '#5E5F66' }}>Notes ({notes.length})</button>
        <button onClick={() => setActiveTab('tasks')} data-testid="tab-tasks" style={{ padding: '12px 24px', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', fontWeight: activeTab === 'tasks' ? 700 : 400, borderBottom: activeTab === 'tasks' ? '2px solid #002FA7' : '2px solid transparent', marginBottom: '-2px', color: activeTab === 'tasks' ? '#002FA7' : '#5E5F66' }}>Taches ({tasks.length})</button>
        {isAdmin && (
          <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6, paddingBottom: 8 }}>
            <Funnel size={14} color="#5E5F66" />
            <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>Filtrer par personne :</span>
            <div style={{ minWidth: 200 }}>
              <Select value={filterUserId} onValueChange={(v) => setFilterUserId(v)}>
                <SelectTrigger data-testid="filter-user-select" style={{ height: 32 }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tout le monde</SelectItem>
                  <SelectItem value="unassigned">Non assigné</SelectItem>
                  {employees.map((u) => (
                    <SelectItem key={u.id} value={u.id} data-testid={`filter-user-${u.id}`}>
                      {u.name}{u.role === 'admin' ? ' (admin)' : ''}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        )}
      </div>

      {activeTab === 'notes' && (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          <FolderSidebar
            folders={noteFolders} currentFolder={currentNoteFolder}
            onSelect={setCurrentNoteFolder}
            onCreate={(n) => createFolder('note', n)}
            onDelete={(id) => deleteFolder('note', id)}
            onEdit={editFolder}
            onDropToFolder={moveNoteToFolder}
            type="note"
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: '16px' }}>
              {currentNoteFolder === 'trash' ? (
                <>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}><Trash size={20} /> Corbeille des notes</h3>
                  <button className="btn btn-secondary" onClick={emptyNotesTrash} data-testid="empty-notes-trash-btn" style={{ color: '#C62828' }}>
                    <Trash size={14} /> Vider la corbeille
                  </button>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {selectMode ? (
                      <>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          {selectedNoteIds.size} selectionnee(s)
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={selectAllVisibleNotes}
                          data-testid="select-all-notes-btn"
                          disabled={notes.length === 0}
                        >Tout selectionner</button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={exitSelectMode}
                          data-testid="cancel-select-notes-btn"
                        >Annuler</button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setSelectMode(true)}
                        data-testid="enter-select-notes-btn"
                        disabled={notes.length === 0}
                        title="Activer la selection multiple"
                      >
                        <CheckSquare size={16} /> Selectionner
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selectMode && (
                      <>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setBulkAction('move-note')}
                          data-testid="bulk-move-notes-btn"
                          disabled={selectedNoteIds.size === 0}
                          title="Deplacer dans un dossier"
                        >
                          <Folder size={16} /> Deplacer ({selectedNoteIds.size})
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setBulkAction('assign-note')}
                          data-testid="bulk-assign-notes-btn"
                          disabled={selectedNoteIds.size === 0}
                          title="Affecter a un salarie"
                        >
                          <UserCircle size={16} /> Affecter ({selectedNoteIds.size})
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={bulkDeleteNotes}
                          data-testid="bulk-delete-notes-btn"
                          disabled={selectedNoteIds.size === 0}
                          style={{
                            backgroundColor: selectedNoteIds.size === 0 ? '#FCA5A5' : '#DC2626',
                            color: 'white',
                            cursor: selectedNoteIds.size === 0 ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <Trash size={16} /> Supprimer ({selectedNoteIds.size})
                        </button>
                      </>
                    )}
                    <button className="btn btn-primary" onClick={createAndOpenNote} data-testid="add-note-btn"><Plus size={16} /> Nouvelle note</button>
                  </div>
                </>
              )}
            </div>
            {notes.length === 0 ? (
              <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>
                <NotePencil size={48} style={{ opacity: 0.4, marginBottom: '8px' }} />
                <p style={{ margin: 0 }}>{currentNoteFolder === 'trash' ? 'Corbeille vide.' : 'Aucune note dans ce dossier.'}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {notes.map(n => {
                  const inTrash = currentNoteFolder === 'trash' || !!n.deleted_at;
                  const accent = '#1565C0';
                  const isSelected = selectedNoteIds.has(n.id);
                  return (
                    <div
                      key={n.id}
                      className="card"
                      data-testid={`note-card-${n.id}`}
                      draggable={!inTrash && !selectMode}
                      onDragStart={(inTrash || selectMode) ? undefined : () => setDragged({ type: 'note', id: n.id, current_folder_id: n.folder_id || '' })}
                      onDragEnd={(inTrash || selectMode) ? undefined : () => setDragged(null)}
                      style={{
                        padding: '12px 16px',
                        borderLeft: `4px solid ${inTrash ? '#9E9EA7' : accent}`,
                        backgroundColor: selectMode && isSelected ? '#EFF6FF' : undefined,
                        outline: selectMode && isSelected ? '2px solid #1565C0' : 'none',
                        opacity: dragged?.type === 'note' && dragged?.id === n.id ? 0.5 : (inTrash ? 0.85 : 1),
                        transition: 'opacity 120ms, background-color 120ms',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'nowrap', gap: '12px', cursor: inTrash && !selectMode ? 'default' : 'pointer', minWidth: 0 }} onClick={(e) => {
                        if (selectMode) { toggleNoteSelected(n.id); return; }
                        if (inTrash) return;
                        openNoteDetail(n.id);
                      }}>
                        {selectMode && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleNoteSelected(n.id)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`select-note-${n.id}`}
                            style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
                          />
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                          {n.photo_url && (
                            <img src={`${API_URL}${n.photo_url}?v=${n.id}`} alt="" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: '6px', flexShrink: 0 }} />
                          )}
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, color: '#1E2030', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{n.title || '(Sans titre)'}</div>
                            {n.content && (
                              <div style={{ fontSize: '0.82rem', color: '#5E5F66', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-testid={`note-preview-${n.id}`}>
                                {firstSentence(n.content)}
                              </div>
                            )}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                          <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{n.date}</span>
                          {n.assigned_user_name && (
                            <span style={{ fontSize: '0.72rem', color: '#1565C0', display: 'flex', alignItems: 'center', gap: '4px' }} title={n.assigned_user_name}>
                              <UserCircle size={14} weight="fill" /> 1
                            </span>
                          )}
                          {inTrash ? (
                            <>
                              <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); restoreNote(n.id); }} data-testid={`restore-note-${n.id}`} title="Restaurer">
                                <ArrowCounterClockwise size={12} />
                              </button>
                              <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); purgeNote(n.id); }} data-testid={`purge-note-${n.id}`} title="Supprimer definitivement" style={{ color: '#C62828' }}>
                                <Trash size={12} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); openNoteDetail(n.id); }} data-testid={`edit-note-${n.id}`}>
                                <PencilSimple size={12} />
                              </button>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Deplacer "${n.title || 'cette note'}" dans la corbeille ?`)) deleteNote(n.id);
                                }}
                                data-testid={`delete-note-${n.id}`}
                                title="Supprimer (corbeille)"
                                style={{ color: '#C62828' }}
                              >
                                <Trash size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {activeTab === 'tasks' && (
        <div style={{ display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
          <FolderSidebar
            folders={taskFolders} currentFolder={currentTaskFolder}
            onSelect={setCurrentTaskFolder}
            onCreate={(n) => createFolder('task', n)}
            onDelete={(id) => deleteFolder('task', id)}
            onEdit={editFolder}
            onDropToFolder={moveTaskToFolder}
            type="task"
          />
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap', marginBottom: '16px' }}>
              {currentTaskFolder === 'trash' ? (
                <>
                  <h3 style={{ margin: 0, fontSize: '1.05rem', display: 'flex', alignItems: 'center', gap: 8 }}><Trash size={20} /> Corbeille des taches</h3>
                  <button className="btn btn-secondary" onClick={emptyTasksTrash} data-testid="empty-tasks-trash-btn" style={{ color: '#C62828' }}>
                    <Trash size={14} /> Vider la corbeille
                  </button>
                </>
              ) : (
                <>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                    {selectMode ? (
                      <>
                        <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>
                          {selectedTaskIds.size} selectionnee(s)
                        </span>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={selectAllVisibleTasks}
                          data-testid="select-all-tasks-btn"
                          disabled={tasks.length === 0}
                        >Tout selectionner</button>
                        <button
                          type="button"
                          className="btn btn-secondary btn-sm"
                          onClick={exitSelectMode}
                          data-testid="cancel-select-tasks-btn"
                        >Annuler</button>
                      </>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setSelectMode(true)}
                        data-testid="enter-select-tasks-btn"
                        disabled={tasks.length === 0}
                        title="Activer la selection multiple"
                      >
                        <CheckSquare size={16} /> Selectionner
                      </button>
                    )}
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {selectMode && (
                      <>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => setBulkAction('move-task')}
                          data-testid="bulk-move-tasks-btn"
                          disabled={selectedTaskIds.size === 0}
                          title="Deplacer dans un dossier"
                        >
                          <Folder size={16} /> Deplacer ({selectedTaskIds.size})
                        </button>
                        <button
                          type="button"
                          className="btn btn-secondary"
                          onClick={() => { setBulkAssignTaskUserIds([]); setBulkAction('assign-task'); }}
                          data-testid="bulk-assign-tasks-btn"
                          disabled={selectedTaskIds.size === 0}
                          title="Affecter aux salaries"
                        >
                          <UserCircle size={16} /> Affecter ({selectedTaskIds.size})
                        </button>
                        <button
                          type="button"
                          className="btn"
                          onClick={bulkDeleteTasks}
                          data-testid="bulk-delete-tasks-btn"
                          disabled={selectedTaskIds.size === 0}
                          style={{
                            backgroundColor: selectedTaskIds.size === 0 ? '#FCA5A5' : '#DC2626',
                            color: 'white',
                            cursor: selectedTaskIds.size === 0 ? 'not-allowed' : 'pointer',
                          }}
                        >
                          <Trash size={16} /> Supprimer ({selectedTaskIds.size})
                        </button>
                      </>
                    )}
                    <button className="btn btn-primary" onClick={() => { setEditingTask(null); setShowTaskModal(true); }} data-testid="add-task-btn"><Plus size={16} /> Nouvelle tache</button>
                  </div>
                </>
              )}
            </div>
            {tasks.length === 0 ? (
              <div className="card" style={{ padding: '40px', textAlign: 'center', color: '#5E5F66' }}>
                <ListChecks size={48} style={{ opacity: 0.4, marginBottom: '8px' }} />
                <p style={{ margin: 0 }}>{currentTaskFolder === 'trash' ? 'Corbeille vide.' : 'Aucune tache dans ce dossier.'}</p>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {tasks.map(t => {
                  const expanded = expandedTaskId === t.id;
                  const pcfg = PRIORITY_CONFIG[t.priority] || PRIORITY_CONFIG.normal;
                  const inTrash = currentTaskFolder === 'trash' || !!t.deleted_at;
                  const isSelected = selectedTaskIds.has(t.id);
                  return (
                    <div
                      key={t.id}
                      className="card"
                      data-testid={`task-card-${t.id}`}
                      draggable={!inTrash && !selectMode}
                      onDragStart={(inTrash || selectMode) ? undefined : () => setDragged({ type: 'task', id: t.id, current_folder_id: t.folder_id || '' })}
                      onDragEnd={(inTrash || selectMode) ? undefined : () => setDragged(null)}
                      style={{
                        padding: '12px 16px',
                        borderLeft: `4px solid ${inTrash ? '#9E9EA7' : t.color}`,
                        backgroundColor: selectMode && isSelected ? '#EFF6FF' : undefined,
                        outline: selectMode && isSelected ? '2px solid #1565C0' : 'none',
                        opacity: dragged?.type === 'task' && dragged?.id === t.id ? 0.5 : (inTrash ? 0.85 : 1),
                        transition: 'opacity 120ms, background-color 120ms',
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'nowrap', gap: '12px', cursor: 'pointer', minWidth: 0 }} onClick={() => {
                        if (selectMode) { toggleTaskSelected(t.id); return; }
                        setExpandedTaskId(expanded ? null : t.id);
                      }}>
                        {selectMode && (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleTaskSelected(t.id)}
                            onClick={(e) => e.stopPropagation()}
                            data-testid={`select-task-${t.id}`}
                            style={{ width: 18, height: 18, cursor: 'pointer', flexShrink: 0 }}
                          />
                        )}
                        <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1, minWidth: 0 }}>
                          <span onClick={(e) => { e.stopPropagation(); if (!inTrash) toggleTaskComplete(t); }} style={{ cursor: inTrash ? 'default' : 'pointer', flexShrink: 0 }}>
                            {t.completed ? <CheckCircle size={22} weight="fill" color="#2E7D32" /> : <Circle size={22} color="#9E9EA7" />}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ fontWeight: 700, textDecoration: t.completed ? 'line-through' : 'none', color: t.completed ? '#9E9EA7' : '#1E2030', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{t.title}</div>
                            {!expanded && t.description && <div style={{ fontSize: '0.82rem', color: '#5E5F66', marginTop: '2px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} data-testid={`task-preview-${t.id}`}>{firstSentence(t.description)}</div>}
                          </div>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexShrink: 0 }}>
                          <span style={{ padding: '2px 8px', borderRadius: '4px', fontSize: '0.72rem', fontWeight: 600, backgroundColor: pcfg.bg, color: pcfg.color, display: 'flex', alignItems: 'center', gap: '4px' }}><Flag size={11} /> {pcfg.label}</span>
                          <span style={{ fontSize: '0.78rem', color: '#5E5F66' }}>{t.date} {t.time}</span>
                          {(t.assigned_user_names || []).length > 0 && (
                            <span style={{ fontSize: '0.72rem', color: '#1565C0', display: 'flex', alignItems: 'center', gap: '4px' }} title={t.assigned_user_names.join(', ')}>
                              <UserCircle size={14} weight="fill" /> {(t.assigned_user_names || []).length}
                            </span>
                          )}
                          {inTrash ? (
                            <>
                              <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); restoreTask(t.id); }} data-testid={`restore-task-${t.id}`} title="Restaurer">
                                <ArrowCounterClockwise size={12} />
                              </button>
                              <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); purgeTask(t.id); }} data-testid={`purge-task-${t.id}`} title="Supprimer definitivement" style={{ color: '#C62828' }}>
                                <Trash size={12} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button className="btn btn-secondary btn-sm" onClick={(e) => { e.stopPropagation(); setEditingTask(t); setShowTaskModal(true); }} data-testid={`edit-task-${t.id}`}><PencilSimple size={12} /></button>
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (window.confirm(`Deplacer "${t.title}" dans la corbeille ?`)) deleteTask(t.id);
                                }}
                                data-testid={`delete-task-${t.id}`}
                                title="Supprimer (corbeille)"
                                style={{ color: '#C62828' }}
                              >
                                <Trash size={12} />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      {expanded && t.description && (
                        <div style={{ marginTop: '10px', padding: '10px', backgroundColor: '#F8F9FB', borderRadius: '6px', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: '0.88rem', color: '#1E2030', maxHeight: '180px', overflowY: 'auto' }} data-testid={`task-content-${t.id}`}>
                          {t.description}
                        </div>
                      )}
                      {expanded && (t.assigned_user_names || []).length > 0 && (
                        <div style={{ marginTop: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
                          {t.assigned_user_names.map((name, i) => (
                            <span key={i} style={{ padding: '3px 10px', borderRadius: '12px', fontSize: '0.75rem', backgroundColor: '#E3F2FD', color: '#1565C0', fontWeight: 600 }}>{name}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {showNoteModal && <NoteModal note={editingNote} folders={noteFolders} employees={employees} onClose={() => setShowNoteModal(false)} onSubmit={submitNote} onDelete={deleteNote} />}
      {showTaskModal && <TaskModal task={editingTask} folders={taskFolders} employees={employees} onClose={() => setShowTaskModal(false)} onSubmit={submitTask} onDelete={deleteTask} />}

      {/* Bulk move (notes) */}
      {bulkAction === 'move-note' && (
        <BulkActionDialog title={`Deplacer ${selectedNoteIds.size} note(s) vers...`} onClose={() => setBulkAction(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button type="button" className="btn btn-secondary" onClick={() => bulkMoveNotes(null)} data-testid="bulk-move-target-none-note">Sans dossier</button>
            {noteFolders.filter(f => !f.is_trash && f.id !== 'trash').map(f => (
              <button
                key={f.id}
                type="button"
                className="btn btn-secondary"
                onClick={() => bulkMoveNotes(f.id)}
                data-testid={`bulk-move-target-note-${f.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-start' }}
              >
                <Folder size={16} color={f.color || '#1565C0'} /> {f.name}
              </button>
            ))}
          </div>
        </BulkActionDialog>
      )}

      {/* Bulk move (tasks) */}
      {bulkAction === 'move-task' && (
        <BulkActionDialog title={`Deplacer ${selectedTaskIds.size} tache(s) vers...`} onClose={() => setBulkAction(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <button type="button" className="btn btn-secondary" onClick={() => bulkMoveTasks(null)} data-testid="bulk-move-target-none-task">Sans dossier</button>
            {taskFolders.filter(f => !f.is_trash && f.id !== 'trash').map(f => (
              <button
                key={f.id}
                type="button"
                className="btn btn-secondary"
                onClick={() => bulkMoveTasks(f.id)}
                data-testid={`bulk-move-target-task-${f.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-start' }}
              >
                <Folder size={16} color={f.color || '#1565C0'} /> {f.name}
              </button>
            ))}
          </div>
        </BulkActionDialog>
      )}

      {/* Bulk assign (notes — single user) */}
      {bulkAction === 'assign-note' && (
        <BulkActionDialog title={`Affecter ${selectedNoteIds.size} note(s) a un salarie`} onClose={() => setBulkAction(null)}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: '50vh', overflowY: 'auto' }}>
            <button type="button" className="btn btn-secondary" onClick={() => bulkAssignNotes(null)} data-testid="bulk-assign-target-none-note">Aucun (retirer l'affectation)</button>
            {employees.map(e => (
              <button
                key={e.id}
                type="button"
                className="btn btn-secondary"
                onClick={() => bulkAssignNotes(e.id)}
                data-testid={`bulk-assign-target-note-${e.id}`}
                style={{ display: 'flex', alignItems: 'center', gap: 8, justifyContent: 'flex-start' }}
              >
                <UserCircle size={16} weight="fill" /> {e.name}
              </button>
            ))}
          </div>
        </BulkActionDialog>
      )}

      {/* Bulk assign (tasks — multi user) */}
      {bulkAction === 'assign-task' && (
        <BulkActionDialog title={`Affecter ${selectedTaskIds.size} tache(s) a...`} onClose={() => setBulkAction(null)}>
          <p style={{ fontSize: '0.82rem', color: '#5E5F66', margin: '0 0 10px 0' }}>
            Selectionnez un ou plusieurs salaries. Les anciennes affectations seront remplacees.
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', maxHeight: '40vh', overflowY: 'auto', padding: '6px', border: '1px solid #E2E2E6', borderRadius: '6px', marginBottom: 12 }}>
            {employees.map(e => {
              const sel = bulkAssignTaskUserIds.includes(e.id);
              return (
                <span
                  key={e.id}
                  onClick={() => toggleBulkAssignTaskUser(e.id)}
                  data-testid={`bulk-assign-task-pill-${e.id}`}
                  style={{ padding: '4px 10px', borderRadius: '12px', fontSize: '0.78rem', cursor: 'pointer', backgroundColor: sel ? '#002FA7' : '#F4F5F7', color: sel ? 'white' : '#1E2030', fontWeight: sel ? 600 : 400 }}
                >
                  {e.name}
                </span>
              );
            })}
          </div>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
            <button type="button" className="btn btn-secondary" onClick={() => setBulkAction(null)}>Annuler</button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => bulkAssignTasks(bulkAssignTaskUserIds)}
              data-testid="bulk-assign-task-confirm"
            >Affecter ({bulkAssignTaskUserIds.length})</button>
          </div>
        </BulkActionDialog>
      )}
    </div>
  );
};

export default NotesTasksPage;
